import { Button, ButtonVariant } from '@vimily/bonjoro-ui-components';
import React, { useRef, useState } from 'react';
import cx from 'classnames';
import { assetUrl } from '~/helpers';
import { first } from 'lodash';
import ImageContainer from './ImageContainer';
import { ImageType } from './Image';
import getUploadUrl, { UploadUrlResponse } from '../Actions/uploadImageAction';
import { getFileExtension, FileType } from '~/utils/file';
import { useUploadImageToS3 } from '~/App/Api/S3Upload';

export interface ImageUploadProps {
  profileKey: string;
  className?: string;
  imageType: ImageType;
  label: string;
  imgUrl?: string;
  onImageChange: (profileKey: string, imageUrl: string) => void;
}

export default function ImageUpload({
  profileKey,
  className,
  imageType,
  label,
  imgUrl,
  onImageChange,
}: ImageUploadProps) {
  const fileInput = useRef<HTMLInputElement>(null);
  const handleUploadClick = () => fileInput.current?.click();

  const [url, setUrl] = useState(imgUrl);

  const { mutateAsync: uploadFileMutation, isLoading: isUploading } =
    useUploadImageToS3();

  const clearFile = () => {
    if (fileInput.current) {
      fileInput.current.value = '';
    }
  };

  const uploadFile = async () => {
    const file = first(fileInput?.current?.files);

    if (file == null) {
      return;
    }
    const extension = getFileExtension(file);
    const fileType = file.type;

    try {
      const res: UploadUrlResponse = await getUploadUrl(extension);
      const url = res.url;
      if (res) {
        await uploadFileMutation({ url, file, fileType });
        setUrl(res.assetUrl);
        onImageChange(profileKey, res.assetUrl);
      }
    } catch (error) {
      clearFile();
    }
  };

  const handleFileChange = () => {
    void uploadFile();
  };

  const acceptedFileTypes = [FileType.Png, FileType.Jpg, FileType.Svg].join(
    ', '
  );

  return (
    <div className={cx('flex', [className])}>
      <div className='mr-2.5'>
        <ImageContainer
          imageType={imageType}
          loading={isUploading}
          imageUrl={url}
        />
      </div>
      <div>
        <div className='mb-[6px] text-base font-bold'>{label}</div>
        <Button
          variant={ButtonVariant.Info}
          className={cx('block', {
            'w-60': imageType == ImageType.Circle,
            'w-44': imageType == ImageType.Square,
          })}
          onClick={handleUploadClick}
        >
          <img
            src={assetUrl('assets/images/manual-upload/upload.svg')}
            className='h-5 w-5 mr-2.5'
          />
          Add an image
          <input
            type='file'
            className='hidden'
            ref={fileInput}
            accept={acceptedFileTypes}
            onChange={handleFileChange}
          />
        </Button>
      </div>
    </div>
  );
}
